import { useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import { dBConn } from '../../ApiCenter/API_GET'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import { ImageViewer } from './whatsNewSupport'

// Button component to be used in MenuLeft
export const WhatsNewButton = ({ onClick, hasNewContent }) => {
    return (
        <li className="d-flex align-items-center" style={{position: 'relative', cursor: 'pointer'}} onClick={onClick}>
            <div style={{position: 'relative', display: 'inline-block'}}>
                <i 
                    className={`bi bi-megaphone me-2`} 
                    style={{
                        color: hasNewContent ? '#fa8703ff' : null,
                        animation: hasNewContent ? 'megaphoneFlip 5s ease-in-out infinite' : 'none',
                        display: 'inline-block',
                        transformStyle: 'preserve-3d'
                    }}
                />
            </div>
            <span className="ms-2">What's New</span>
            <style>{`
                @keyframes megaphoneFlip {
                    0%, 80% {
                        transform: rotateY(0deg);
                    }
                    90% {
                        transform: rotateY(180deg);
                    }
                    100% {
                        transform: rotateY(360deg);
                    }
                }`}</style>
        </li>
    )
}

// Offcanvas component with imperative handle
export const WhatsNewPublic = forwardRef(({ userType = 'internal' }, ref) => {
    const [isOpen, setIsOpen] = useState(false)
    const [newsList, setNewsList] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [imagesList, setImagesList] = useState([])
    const [imageViewerOpen, setImageViewerOpen] = useState(false)
    const [viewingImages, setViewingImages] = useState([])
    const [currentImageIndex, setCurrentImageIndex] = useState(0)
    const [hasNewContent, setHasNewContent] = useState(false)

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
        toggle: () => setIsOpen(prev => !prev),
        open: () => setIsOpen(true),
        close: () => setIsOpen(false),
        hasNewContent: hasNewContent
    }))

    // Load full details when offcanvas is opened
    useEffect(() => {
        if(isOpen){
            setIsLoading(true)
            dBConn('whatsNew-Public', {fullDetails: true}).then(response => {
                if (response.success) {
                    // Mark features as new if visibilityfrom > user's whatsnew_date
                    const processedData = response.data.map(feature => {
                        const userLastView = feature.whatsnew_date
                        const featurePublishDate = feature.visibilityfrom
                        
                        // If user never viewed OR feature is newer than last view
                        const isNew = !userLastView || new Date(featurePublishDate) > new Date(userLastView)
                        
                        return { ...feature, isNew }
                    })
                    
                    setNewsList(processedData)
                    setImagesList(response.images || [])
                    setIsLoading(false)
                    setHasNewContent(false) // Reset badge when user views content
                }
            })
        }
    }, [isOpen])

    // Polling for new content notifications when closed
    useEffect(() => {
        // Initial check
        const checkForNewContent = () => {
            dBConn('whatsNew-Public', {fullDetails: false}).then(response => {
                if (response.success) {
                    setHasNewContent(response.hasNewContent)
                }
            })
        }

        // First call after 5 seconds
        const initialTimeout = setTimeout(checkForNewContent, 5000)

        // Poll every 5 minutes
        const interval = setInterval(checkForNewContent, 60000 * 3)

        return () => {
            clearTimeout(initialTimeout)
            clearInterval(interval)
        }
    }, [])

    const closeOffcanvas = () => {
        setIsOpen(false)
    }

    const openImageViewer = (images, startIndex = 0) => {
        setImageViewerOpen(true)
        setViewingImages(images)
        setCurrentImageIndex(startIndex)
    }

    const closeImageViewer = () => {
        setImageViewerOpen(false)
        setViewingImages([])
        setCurrentImageIndex(0)
    }

    const nextImage = () => {
        setCurrentImageIndex((currentImageIndex + 1) % viewingImages.length)
    }

    const prevImage = () => {
        setCurrentImageIndex(currentImageIndex === 0 ? viewingImages.length - 1 : currentImageIndex - 1)
    }

    return (
        <>
            {/* Backdrop */}
            {isOpen && (
                <div className="offcanvas-backdrop fade show" 
                    onClick={closeOffcanvas}
                    style={{ zIndex: 1040 }}
                ></div>
            )}

            {/* Offcanvas */}
            <div 
                className={`offcanvas offcanvas-end ${isOpen ? 'show' : ''}`}
                tabIndex="-1"
                style={{
                    visibility: isOpen ? 'visible' : 'hidden',
                    width: '500px',
                    zIndex: 1045,
                    backgroundColor: '#fff',
                    top: '60px'
                }}
            >
                {/* Header */}
                <div className="offcanvas-header" style={{
                    borderBottom: '1px solid #dee2e6',
                    padding: '10px'
                }}>
                    <div>
                        <h5 className="offcanvas-title d-flex align-items-center mb-1" style={{
                            fontSize: '20px',
                            fontWeight: '600',
                            color: '#212529'
                        }}>
                            <i className="bi bi-megaphone me-2" style={{ color: '#ff6b35', fontSize: '24px' }}></i>
                            What's New in AudioMate
                        </h5>
                    </div>
                    <button 
                        type="button" 
                        className="btn-close" 
                        onClick={closeOffcanvas}
                        style={{ fontSize: '12px' }}
                    ></button>
                </div>

                {/* Body */}
                <div className="offcanvas-body" style={{
                    padding: '10px',
                    overflowY: 'auto',
                    backgroundColor: '#f8f9fa'
                }}>
                    {isLoading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : newsList.length === 0 ? (
                        <div className="text-center py-5" style={{color: '#6c757d'}}>
                            <i className="bi bi bi-megaphone me-2" style={{fontSize: '48px', color: '#CCC'}}></i>
                            <p className="mt-3">No news available</p>
                        </div>
                    ) : (
                        newsList.map((item) => {
                            return ( <div  key={item.id} className="mb-2 p-2"
                                    style={{
                                        backgroundColor: '#fff',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '8px',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                                    }}
                                >
                                    {/* Card Header */}
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <h6 className="mb-0" style={{fontSize: '16px',fontWeight: '600', color: '#212529'}}>
                                            {item.isNew && (
                                                <span className="badge bg-danger me-2" style={{fontSize: '9px', verticalAlign: 'middle'}}>
                                                    NEW
                                                </span>
                                            )}
                                            {item.title}
                                        </h6>
                                        <span style={{fontSize: '11px', color: '#6c757d', whiteSpace: 'nowrap', marginLeft: '12px'}}>
                                            {dayjs(item.date).format('D-MMM-YYYY')}
                                        </span>
                                    </div>

                                    {/* Description */}
                                    <div 
                                        className="mb-2" style={{fontSize: '13px', color: '#495057', lineHeight: '1.5'}}
                                        dangerouslySetInnerHTML={{ __html: item.description }}
                                    />

                                    {/* Feature Images Thumbnails */}
                                    {item.images_ids && (() => {
                                        const imageIds = String(item.images_ids).split(',').map(id => parseInt(id.trim()))
                                        const featureImages = imagesList.filter(img => imageIds.includes(img.id))
                                        
                                        // Show skeleton if images are expected but not loaded yet
                                        if (imageIds.length > 0 && featureImages.length === 0 && isLoading) {
                                            return (
                                                <div className="d-flex gap-2 mt-2">
                                                    {imageIds.map((id) => (
                                                        <div 
                                                            key={id}
                                                            style={{
                                                                width: '50px',
                                                                height: '50px',
                                                                border: '1px solid #dee2e6',
                                                                borderRadius: '4px',
                                                                overflow: 'hidden',
                                                                backgroundColor: '#e9ecef',
                                                                animation: 'pulse 1.5s ease-in-out infinite'
                                                            }}
                                                        />
                                                    ))}
                                                </div>
                                            )
                                        }
                                        
                                        if (featureImages.length > 0) {
                                            return (
                                                <div className="d-flex gap-2 mt-2">
                                                    {featureImages.map((img, imgIndex) => (
                                                        <div 
                                                            key={img.id}
                                                            onClick={() => openImageViewer(featureImages, imgIndex)}
                                                            style={{
                                                                width: '50px',
                                                                height: '50px',
                                                                border: '1px solid #dee2e6',
                                                                borderRadius: '4px',
                                                                overflow: 'hidden',
                                                                cursor: 'pointer',
                                                                transition: 'transform 0.2s'
                                                            }}
                                                            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                                        >
                                                            <img 
                                                                src={img.s3Info} 
                                                                alt={img.image_name}
                                                                style={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover'
                                                                }}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            )
                                        }
                                        return null
                                    })()}
                                </div>
                            )
                        })
                    )}
                </div>

                {/* Footer */}
                <div className="offcanvas-footer" style={{borderTop: '1px solid #dee2e6', padding: '0px', margin: '0px', backgroundColor: '#fff'}}>
                    <Link to="/help" className="btn btn-link w-100 text-center"
                        style={{fontSize: '13px', textDecoration: 'none', color: '#0d6efd'}}
                        onClick={closeOffcanvas}
                    >
                        <i className="bi bi-book me-2"></i>
                        See the Complete Online Help page →
                    </Link>
                </div>
            </div>

            {/* Image Viewer Modal */}
            <ImageViewer
                isOpen={imageViewerOpen}
                onClose={closeImageViewer}
                images={viewingImages}
                currentIndex={currentImageIndex}
                onNext={nextImage}
                onPrev={prevImage}
            />
        </>
    )
})
